import { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useAuth } from "../../hooks/AuthProvider";
import { AI_AVAILABLE_MODELS_ENDPOINT } from "../../config/urls";
import { aiFetch, goToIntegrations } from "./aiApi";

const DEFAULT = "__default__";

/**
 * Picks where an AI feature's model comes from: BotBridge keys or the tenant's own
 * key, then provider and model. Options come from the backend, so it only ever offers
 * what the tenant can actually use (catalog ∩ the key's accessible models).
 *
 * value: { source, provider, model } — provider/model "" mean "default".
 *
 * Props:
 *  - capability: "chat" | "embed"
 *  - allowTenantDefault: show "Use tenant default" (per-bot / per-node override);
 *    selecting it calls onChange(null)
 *  - source / hideSource: let a parent own the source toggle (AI settings panel)
 *  - allowPlatformDefault: offer a "BotBridge default" provider option ("" provider)
 */
export default function AIModelPicker({
  value,
  onChange,
  capability = "chat",
  allowTenantDefault = false,
  source: sourceProp,
  hideSource = false,
  allowPlatformDefault = false,
  platformDefaultLabel = "BotBridge default",
  disabled = false,
  label = "AI model",
  defaultLabel = "Use the default AI model (Agentic AI → Configuration)",
}) {
  const { getValidToken } = useAuth();
  const useDefault = allowTenantDefault && !value;
  const source = sourceProp || value?.source || "platform";

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (useDefault) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const params = new URLSearchParams({ source, capability });
      const res = await aiFetch(getValidToken, `${AI_AVAILABLE_MODELS_ENDPOINT}?${params}`);
      if (!cancelled) {
        setProviders(res.ok ? res.data?.providers || [] : []);
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, capability, useDefault]);

  const provider = value?.provider || "";
  const model = value?.model || "";
  const selectedProvider = providers.find((p) => p.slug === provider);
  const providerMissing = !!provider && !loading && !selectedProvider;

  const emit = (next) => onChange({ source, provider, model, ...next });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {allowTenantDefault && (
        <FormControl size="small" fullWidth disabled={disabled}>
          <InputLabel>{label}</InputLabel>
          <Select
            label={label}
            value={useDefault ? DEFAULT : "custom"}
            onChange={(e) =>
              e.target.value === DEFAULT
                ? onChange(null)
                : onChange({ source: "platform", provider: "", model: "" })
            }
          >
            <MenuItem value={DEFAULT}>{defaultLabel}</MenuItem>
            <MenuItem value="custom">Choose a model for this feature</MenuItem>
          </Select>
        </FormControl>
      )}

      {!useDefault && (
        <>
          {!hideSource && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={source}
              disabled={disabled}
              onChange={(e, next) => next && onChange({ source: next, provider: "", model: "" })}
            >
              <ToggleButton value="platform">BotBridge keys</ToggleButton>
              <ToggleButton value="own">My own key</ToggleButton>
            </ToggleButtonGroup>
          )}

          {loading ? (
            <CircularProgress size={20} />
          ) : source === "own" && providers.length === 0 && !allowPlatformDefault ? (
            <Typography variant="body2" color="text.secondary">
              No AI provider is connected with your own key yet.{" "}
              <Link component="button" onClick={goToIntegrations}>
                Connect a provider on the Integrations page
              </Link>
              .
            </Typography>
          ) : (
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl size="small" fullWidth disabled={disabled}>
                <InputLabel shrink>Provider</InputLabel>
                <Select
                  label="Provider"
                  notched
                  value={provider}
                  onChange={(e) => emit({ provider: e.target.value, model: "" })}
                  displayEmpty
                  renderValue={(v) =>
                    v === ""
                      ? allowPlatformDefault
                        ? platformDefaultLabel
                        : "Choose a provider"
                      : providers.find((p) => p.slug === v)?.name || v
                  }
                >
                  {allowPlatformDefault && <MenuItem value="">{platformDefaultLabel}</MenuItem>}
                  {providers.map((p) => (
                    <MenuItem key={p.slug} value={p.slug}>
                      {p.name}
                    </MenuItem>
                  ))}
                  {providerMissing && (
                    <MenuItem value={provider} disabled>
                      {provider} (not available)
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth disabled={disabled || !provider}>
                <InputLabel shrink>Model</InputLabel>
                <Select
                  label="Model"
                  value={model}
                  displayEmpty
                  notched
                  onChange={(e) => emit({ model: e.target.value })}
                  renderValue={(v) => {
                    if (!provider) return "—";
                    if (!v) {
                      const def = selectedProvider?.models.find((m) => m.model_id === selectedProvider?.default_model);
                      return `Provider default${def ? ` (${def.display_name})` : ""}`;
                    }
                    return selectedProvider?.models.find((m) => m.model_id === v)?.display_name || `${v} (not available)`;
                  }}
                >
                  <MenuItem value="">Provider default</MenuItem>
                  {(selectedProvider?.models || []).map((m) => (
                    <MenuItem key={m.model_id} value={m.model_id}>
                      {m.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {providerMissing && (
            <Typography variant="caption" color="error.main">
              {source === "own"
                ? "This provider is no longer connected with your own key. "
                : "This provider is not available on BotBridge keys. "}
              <Link component="button" variant="caption" onClick={goToIntegrations}>
                Manage AI integrations
              </Link>
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
